/**
 * WeaveIn.ai Platform — Duplicate Member Review (TRUS-425 / TRUS-426)
 *
 * Duplicate profiles reach an org two ways: a legacy migration that carries over
 * records the org had already resolved in its old system, and roster imports that
 * re-add people already on file. Before this screen there was no way for staff to
 * see or resolve them — IFR reported one member with four profiles and stopped
 * entering progress notes because they could not tell which profile would survive.
 *
 * The panel answers exactly that: which record is the keeper, what each one holds,
 * and what a merge would move. Merging is non-destructive — content moves onto the
 * survivor and the duplicate is archived, never deleted — so documenting on the
 * survivor is always safe.
 *
 * TRUS-558 merge v2: merging is a guided flow (duplicates/MergeWizard — fields,
 * cases, confirm) and every merge is journaled and revertable from the Merge
 * history tab (duplicates/MergeHistory).
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Users,
  AlertTriangle,
  Check,
  X,
  Loader2,
  RefreshCw,
  Archive,
  Info,
  Download,
  History,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  membersAPI,
  type DuplicateGroup,
  type DuplicateCandidateMember,
  type DuplicateConfidence,
  type DuplicateMergeResult,
} from '@/services/api/members';
import MergeWizard from './duplicates/MergeWizard';
import MergeHistory from './duplicates/MergeHistory';
import { useTabbedChrome } from '@/components/weavein';

interface MemberDuplicatesPanelProps {
  /** Open a member profile from the review list. */
  onOpenMember?: (memberId: number) => void;
  /** Called after any resolution so the caller can refresh its own member list. */
  onResolved?: () => void;
}

const CONFIDENCE_STYLES: Record<DuplicateConfidence, string> = {
  high: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

function fullName(m: DuplicateCandidateMember): string {
  const n = [m.first_name, m.last_name].filter(Boolean).join(' ').trim();
  return n || `(no name on file) #${m.id}`;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

/**
 * TRUS-586: opening a member unmounts this panel (the screen is a tab toggle, not a
 * router route), so hitting Back remounted a fresh panel that refetched from empty and
 * rendered at scroll top — dropping the reviewer at the head of a long list every time.
 *
 * We stash the last loaded list and the scroll position at navigate-away in
 * sessionStorage, keyed to this panel. On the Back remount we hydrate from it without a
 * refetch and restore scroll, then CONSUME the cache (remove it) so only the
 * open-member → Back round-trip restores — any other fresh entry still loads live data.
 */
const PANEL_CACHE_KEY = 'trus586:duplicates-panel';

interface DuplicatesPanelCache {
  minConfidence: DuplicateConfidence;
  groups: DuplicateGroup[];
  survivors: Record<string, number>;
  tab: 'review' | 'history';
  search: string;
  scrollY: number;
}

function readPanelCache(): DuplicatesPanelCache | null {
  try {
    const raw = sessionStorage.getItem(PANEL_CACHE_KEY);
    return raw ? (JSON.parse(raw) as DuplicatesPanelCache) : null;
  } catch {
    return null;
  }
}

function writePanelCache(cache: DuplicatesPanelCache): void {
  try {
    sessionStorage.setItem(PANEL_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* storage unavailable (private mode / opaque origin) — degrade to a refetch */
  }
}

function clearPanelCache(): void {
  try {
    sessionStorage.removeItem(PANEL_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

const MemberDuplicatesPanel: React.FC<MemberDuplicatesPanelProps> = ({
  onOpenMember,
  onResolved,
}) => {
  const tabbed = useTabbedChrome();
  const { t } = useTranslation();
  // Hydrate from the Back-navigation cache (TRUS-586) if one is waiting; otherwise a
  // fresh entry that loads live data.
  const [restored] = useState<DuplicatesPanelCache | null>(() => readPanelCache());
  const [groups, setGroups] = useState<DuplicateGroup[]>(restored?.groups ?? []);
  const [loading, setLoading] = useState(restored == null);
  const [error, setError] = useState<string | null>(null);
  const [minConfidence, setMinConfidence] = useState<DuplicateConfidence>(
    restored?.minConfidence ?? 'low'
  );
  /** Free-text filter over group label + each candidate's name/number (TRUS-586). */
  const [search, setSearch] = useState(restored?.search ?? '');

  /** Chosen survivor per group, keyed by match_key. */
  const [survivors, setSurvivors] = useState<Record<string, number>>(
    restored?.survivors ?? {}
  );
  /** match_key currently being dismissed, so only that row shows a spinner. */
  const [busyKey, setBusyKey] = useState<string | null>(null);
  /** match_key whose guided merge is open. */
  const [mergingKey, setMergingKey] = useState<string | null>(null);
  const [tab, setTab] = useState<'review' | 'history'>(restored?.tab ?? 'review');
  const [notice, setNotice] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  /** On a Back remount we hydrated from cache, so skip the initial fetch. */
  const skipInitialLoad = useRef(restored != null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await membersAPI.listDuplicateMembers({
        min_confidence: minConfidence,
        limit: 200,
      });
      setGroups(res.groups || []);
      // Seed each group's selection with the server's recommendation.
      const seeded: Record<string, number> = {};
      (res.groups || []).forEach((g) => {
        if (g.suggested_survivor_id) seeded[g.match_key] = g.suggested_survivor_id;
      });
      setSurvivors(seeded);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          'Could not load duplicate members.'
      );
    } finally {
      setLoading(false);
    }
  }, [minConfidence]);

  useEffect(() => {
    // A Back remount already has the list from cache — refetching here is exactly what
    // reset the scroll to the top, so skip it once (TRUS-586).
    if (skipInitialLoad.current) {
      skipInitialLoad.current = false;
      return;
    }
    void load();
  }, [load]);

  // Restore the scroll position captured when the reviewer opened a member, once the
  // cached list has painted, then consume the cache so only this round-trip restores.
  useLayoutEffect(() => {
    if (!restored) return;
    clearPanelCache();
    const y = restored.scrollY;
    const id = requestAnimationFrame(() => window.scrollTo(0, y));
    return () => cancelAnimationFrame(id);
    // Mount-only: `restored` is a one-shot snapshot taken at first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Capture the list + scroll before leaving to a member, so Back can restore them. */
  const handleOpenMember = useCallback(
    (id: number) => {
      writePanelCache({
        minConfidence,
        groups,
        survivors,
        tab,
        search,
        scrollY: window.scrollY,
      });
      onOpenMember?.(id);
    },
    [minConfidence, groups, survivors, tab, search, onOpenMember]
  );

  /**
   * Download the candidates as a spreadsheet. Resolving a migration backlog happens
   * away from this screen — the team divides the list up, annotates it, and only then
   * comes back to merge — so the file has to carry every field, not just the four
   * columns shown here (TRUS-518).
   */
  const handleExport = useCallback(async () => {
    setExporting(true);
    setError(null);
    try {
      const blob = await membersAPI.exportDuplicateMembers({
        min_confidence: minConfidence,
        limit: 2000,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `duplicate-members-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail || e?.message || 'Could not export the duplicate list.'
      );
    } finally {
      setExporting(false);
    }
  }, [minConfidence]);

  const totalDuplicates = useMemo(
    () => groups.reduce((n, g) => n + Math.max(0, g.members.length - 1), 0),
    [groups]
  );

  /**
   * Client-side name filter (TRUS-586). The list is already fully in memory (capped at
   * 200 groups by load()), so a reviewer checking one member no longer has to scan the
   * whole list — they type the name and only matching groups render.
   */
  const visibleGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) =>
        g.label.toLowerCase().includes(q) ||
        g.members.some(
          (m) =>
            fullName(m).toLowerCase().includes(q) ||
            (m.member_number || '').toLowerCase().includes(q)
        )
    );
  }, [groups, search]);

  const duplicateIdsFor = (g: DuplicateGroup, survivorId: number) =>
    g.members.map((m) => m.id).filter((id) => id !== survivorId);

  const handleMerged = (g: DuplicateGroup, result: DuplicateMergeResult) => {
    const dupes = Object.keys(result.merged || {}).length;
    const skipped = (result.skipped || []).filter((k) => k.reason === 'unique_conflict').length;
    setGroups((gs) => gs.filter((x) => x.match_key !== g.match_key));
    setMergingKey(null);
    setNotice(
      t('members.duplicates.mergedNotice', {
        count: dupes,
        id: result.survivor_id,
        defaultValue:
          'Merged {{count}} profile(s) into member {{id}}. You can undo this from Merge history.',
      }) +
        (skipped > 0
          ? ' ' +
            t('members.duplicates.mergedSkipped', {
              count: skipped,
              defaultValue:
                '{{count}} item(s) were left on the archived record because the kept record already has an equivalent one.',
            })
          : '')
    );
    onResolved?.();
  };

  const handleDismiss = async (g: DuplicateGroup) => {
    setBusyKey(g.match_key);
    setError(null);
    try {
      await membersAPI.dismissDuplicateMembers({
        member_ids: g.members.map((m) => m.id),
        match_reason: g.reason,
        notes: 'Reviewed and confirmed to be different people.',
      });
      setGroups((gs) => gs.filter((x) => x.match_key !== g.match_key));
      setNotice(
        t(
          'members.duplicates.dismissedNotice',
          'Marked as different people — this group will not be shown again.'
        )
      );
      onResolved?.();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not dismiss this group.');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          {!tabbed && (
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Users className="w-5 h-5" />
            {t('members.duplicates.title', 'Duplicate members')}
          </h2>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            {loading
              ? t('members.duplicates.checking', 'Checking for duplicate profiles…')
              : t('members.duplicates.groupCount', {
                  groups: groups.length,
                  dups: totalDuplicates,
                  defaultValue: '{{groups}} group(s) to review · {{dups}} duplicate profile(s)',
                })}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t(
              'members.duplicates.exportExplainer',
              'The export lists every record in each group, followed by a “merged result” row showing the profile a merge would leave and which fields differ.'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            className="h-9 w-44 rounded-md border border-input bg-background px-3 text-sm"
            placeholder={t('members.duplicates.searchPlaceholder', 'Search by name…')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t(
              'members.duplicates.searchAria',
              'Search duplicate members by name'
            )}
          />
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={minConfidence}
            onChange={(e) => setMinConfidence(e.target.value as DuplicateConfidence)}
            aria-label="Minimum match confidence"
          >
            <option value="high">Strong matches only</option>
            <option value="medium">Strong and likely matches</option>
            <option value="low">All possible matches</option>
          </select>
          <Button
            variant="secondary"
            icon={
              exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )
            }
            onClick={() => void handleExport()}
            disabled={loading || exporting || groups.length === 0}
          >
            {t('members.duplicates.exportButton', 'Export with merged results (CSV)')}
          </Button>
          <Button
            variant="secondary"
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={() => void load()}
            disabled={loading}
          >
            {t('members.duplicates.refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      <div className="mb-4 flex gap-1 border-b" role="tablist">
        {(
          [
            ['review', t('members.duplicates.tabReview', 'Review duplicates'), Users],
            ['history', t('members.duplicates.tabHistory', 'Merge history'), History],
          ] as const
        ).map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${
              tab === key
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setTab(key)}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {notice && (
        <Alert className="mb-4">
          <Check className="w-4 h-4" />
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      {tab === 'review' && (
        <>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" />
            Looking for duplicates…
          </div>
        )}

        {!loading && groups.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center">
              <Check className="w-8 h-8 mx-auto mb-3 text-green-600" />
              <p className="font-medium">No duplicate profiles to review</p>
              <p className="text-sm text-muted-foreground mt-1">
                Every member in this organization looks distinct at the selected
                match strength.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && groups.length > 0 && visibleGroups.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center">
              <Users className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium">
                {t('members.duplicates.noSearchMatches', 'No groups match your search')}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t(
                  'members.duplicates.noSearchMatchesHint',
                  'No duplicate group here contains a member matching “{{q}}”. Clear the search to see them all.',
                  { q: search.trim() }
                )}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Groups */}
        <div className="space-y-4">
          {visibleGroups.map((g) => {
            const survivorId = survivors[g.match_key];
            const busy = busyKey === g.match_key;
            const merging = mergingKey === g.match_key && !!survivorId;

            return (
              <Card key={g.match_key}>
                <CardContent className="p-4 md:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <div className="flex items-center gap-2">
                      <Badge className={CONFIDENCE_STYLES[g.confidence]}>
                        {g.confidence === 'high'
                          ? 'Strong match'
                          : g.confidence === 'medium'
                          ? 'Likely match'
                          : 'Possible match'}
                      </Badge>
                      <span className="text-sm text-muted-foreground">{g.label}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {g.members.length} profiles
                    </span>
                  </div>

                  {/* Side-by-side profiles. Staff pick the record to keep. */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {g.members.map((m) => {
                      const isSurvivor = survivorId === m.id;
                      return (
                        <div
                          key={m.id}
                          className={`rounded-lg border p-3 transition-colors ${
                            isSurvivor
                              ? 'border-primary ring-1 ring-primary/40 bg-primary/5'
                              : 'border-border'
                          }`}
                        >
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              className="mt-1"
                              name={`survivor-${g.match_key}`}
                              checked={isSurvivor}
                              onChange={() =>
                                setSurvivors((s) => ({ ...s, [g.match_key]: m.id }))
                              }
                            />
                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate">{fullName(m)}</div>
                              <div className="text-xs text-muted-foreground">
                                #{m.id}
                                {m.member_number ? ` · ${m.member_number}` : ''}
                              </div>
                            </div>
                          </label>

                          {/* The record was already resolved once, in the old system.
                              Surfacing it stops staff keeping the wrong profile. */}
                          {m.legacy_is_deleted && (
                            <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                              <Archive className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                              <span>Deleted in the legacy system — usually not the record to keep</span>
                            </div>
                          )}

                          <dl className="mt-3 space-y-1 text-xs">
                            <div className="flex justify-between gap-2">
                              <dt className="text-muted-foreground">Date of birth</dt>
                              <dd className="truncate">{formatDate(m.dob)}</dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt className="text-muted-foreground">Phone</dt>
                              <dd className="truncate">{m.phone || '—'}</dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt className="text-muted-foreground">Email</dt>
                              <dd className="truncate">{m.email || '—'}</dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt className="text-muted-foreground">Added</dt>
                              <dd className="truncate">{formatDate(m.created_at)}</dd>
                            </div>
                          </dl>

                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {([
                              ['Documents', m.documents],
                              ['Notes', m.notes],
                              ['Messages', m.communications],
                              ['Forms', m.forms],
                            ] as const).map(([label, n]) =>
                              n > 0 ? (
                                <span
                                  key={label}
                                  className="text-[11px] px-1.5 py-0.5 rounded bg-muted"
                                >
                                  {n} {label.toLowerCase()}
                                </span>
                              ) : null
                            )}
                            {m.content_total === 0 && (
                              <span className="text-[11px] text-muted-foreground">
                                No documents, notes or forms
                              </span>
                            )}
                          </div>

                          {onOpenMember && (
                            <button
                              type="button"
                              className="mt-3 text-xs text-primary hover:underline"
                              onClick={() => handleOpenMember(m.id)}
                            >
                              Open profile
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {merging && (
                    <MergeWizard
                      key={`${g.match_key}-${survivorId}`}
                      survivorId={survivorId}
                      duplicateIds={duplicateIdsFor(g, survivorId)}
                      matchReason={g.reason}
                      onCancel={() => setMergingKey(null)}
                      onMerged={(result) => handleMerged(g, result)}
                    onKeptBoth={() => {
                      setGroups((gs) => gs.filter((x) => x.match_key !== g.match_key));
                      setMergingKey(null);
                      setNotice(
                        t(
                          'members.duplicates.keptBothNotice',
                          'Kept both profiles — marked as different people. You can undo this from Merge history.'
                        )
                      );
                      onResolved?.();
                    }}
                    />
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Button
                      variant="primary"
                      disabled={!survivorId || busy || merging}
                      onClick={() => setMergingKey(g.match_key)}
                      icon={<Check className="w-4 h-4" />}
                    >
                      {t('members.duplicates.mergeStart', 'Keep selected, merge the rest…')}
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() => void handleDismiss(g)}
                      icon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                    >
                      {t('members.duplicates.differentPeople', 'Different people')}
                    </Button>
                  </div>

                  <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    {t(
                      'members.duplicates.mergeExplainerV2',
                      'Merging moves cases, documents, notes, messages and other records onto the record you keep, then archives the others. Nothing is deleted, and every merge can be reverted from Merge history. You choose which value wins where the records disagree, and what happens to cases in the same program.'
                    )}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
        </>
      )}

      {tab === 'history' && (
        <MergeHistory
          onReverted={() => {
            void load();
            onResolved?.();
          }}
        />
      )}
    </div>
  );
};

export default MemberDuplicatesPanel;
